import React, { useState, useEffect } from 'react';
import { Loader, X, AlertCircle, ChevronLeft } from 'lucide-react';
import { CardEditor } from './CardEditor';
import { supabase } from '../lib/supabase';
import { debug, info, error as logError, network } from '../lib/logger';
import { CardOccasion, OccasionGuide, DefaultGuide, OccasionGuideType } from '../types/cards';

interface CardSelectorProps {
  occasion: CardOccasion | null;
  contact: {
    id: string;
    name: string;
    relationship: string;
    card_preference: string;
    address_line1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
    gender: 'male' | 'female' | null;
  };
  onSelect: (cardId: string, message: string) => void;
  onClose: () => void;
  subscriptionStatus?: any;
  userCredits?: number;
  onApproveCard?: (data: {
    front: string;
    back: string;
    to: {
      name: string;
      address_line1: string;
      address_city: string;
      address_state: string;
      address_zip: string;
      address_country?: string;
    };
  }) => void;
}

// Message generation guidelines for different occasions
const OCCASION_GUIDELINES: Record<CardOccasion, OccasionGuide> = {
  'birthday': {
    tone: 'warm and celebratory',
    guidelines: {
      structure: 'natural and flowing',
      length: 'moderate',
      style: 'personal and joyful',
      focus: 'celebration and well-wishes',
      avoid: 'age-related jokes or clichés'
    }
  },
  'anniversary': {
    tone: 'romantic and heartfelt',
    guidelines: {
      structure: 'natural and flowing',
      length: 'moderate',
      style: 'intimate and meaningful',
      focus: 'shared memories and love',
      avoid: 'generic phrases'
    }
  },
  'thank-you': {
    tone: 'grateful and sincere',
    guidelines: {
      structure: 'clear and specific',
      length: 'moderate',
      style: 'personal and appreciative',
      focus: 'specific actions or gifts being thanked for',
      avoid: 'vague expressions of gratitude'
    }
  },
  'graduation': {
    tone: 'proud and encouraging',
    guidelines: {
      structure: 'natural and uplifting',
      length: 'moderate',
      style: 'inspirational and supportive',
      focus: 'achievement and future opportunities',
      avoid: 'clichéd advice'
    }
  },
  'new-baby': {
    tone: 'joyful and warm',
    guidelines: {
      structure: 'gentle and sweet',
      length: 'moderate',
      style: 'tender and celebratory',
      focus: 'welcoming the new arrival',
      avoid: 'parenting advice or sleep jokes'
    }
  },
  'wedding': {
    tone: 'celebratory and warm',
    guidelines: {
      structure: 'elegant and flowing',
      length: 'moderate',
      style: 'formal yet personal',
      focus: 'love and future happiness',
      avoid: 'marriage advice or clichés'
    }
  },
  'appreciation': {
    tone: 'warm and sincere',
    guidelines: {
      structure: 'natural and specific',
      length: 'moderate',
      style: 'personal and heartfelt',
      focus: 'impact and value of the person',
      avoid: 'generic compliments'
    }
  },
  'congratulations': {
    tone: 'enthusiastic and supportive',
    guidelines: {
      structure: 'upbeat and positive',
      length: 'moderate',
      style: 'celebratory and personal',
      focus: 'specific achievement or milestone',
      avoid: 'comparison to others'
    }
  },
  'promotion': {
    tone: 'professional yet warm',
    guidelines: {
      structure: 'clear and encouraging',
      length: 'moderate',
      style: 'professional and supportive',
      focus: 'achievement and new opportunities',
      avoid: 'salary or office politics'
    }
  },
  'new-home': {
    tone: 'warm and excited',
    guidelines: {
      structure: 'welcoming and positive',
      length: 'moderate',
      style: 'friendly and supportive',
      focus: 'new beginnings and well-wishes',
      avoid: 'moving stress or renovation advice'
    }
  },
  'bulk': {
    tone: 'professional and friendly',
    guidelines: {
      structure: 'clear and concise',
      length: 'moderate',
      style: 'professional yet personal',
      focus: 'general message',
      avoid: 'overly specific details'
    }
  },
  'general': {
    tone: 'warm and sincere',
    guidelines: {
      structure: 'natural and flowing',
      length: 'moderate',
      style: 'personal and meaningful',
      focus: 'relationship and occasion',
      avoid: 'clichés and generic phrases'
    }
  }
} as const;

const DEFAULT_GUIDELINES: DefaultGuide = {
  title: 'Select a Card',
  description: 'Choose a card that matches the occasion',
  suggestions: []
};

// Add type guard function
function isOccasionGuide(guide: OccasionGuide | DefaultGuide): guide is OccasionGuide {
  return 'tone' in guide && 'guidelines' in guide;
}

export function CardSelector({ 
  occasion, 
  contact, 
  onSelect, 
  onClose,
  subscriptionStatus,
  userCredits,
  onApproveCard
}: CardSelectorProps) {
  if (!occasion) {
    return null;
  }

  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [cardImages, setCardImages] = useState<string[]>([]);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState<string>('');
  const initRef = React.useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    info('Starting initialization sequence');
    
    const initializeCard = async () => {
      try {
        await fetchUserProfile();
        await generateInitialMessage();
        await generateAllCardImages();
      } catch (error) {
        logError('Initialization error:', error);
        setError('Failed to initialize card creation. Please try again.');
      }
    };

    initRef.current = true;
    initializeCard();
  }, []);

  async function generateInitialMessage() {
    setIsLoadingMessage(true);
    try {
      const occasionGuide = occasion ? OCCASION_GUIDELINES[occasion] : DEFAULT_GUIDELINES;

      const messageParams = {
        occasion,
        recipient: contact.name,
        relationship: contact.relationship,
        gender: contact.gender,
        tone: isOccasionGuide(occasionGuide) ? occasionGuide.tone : 'warm and sincere',
        guidelines: isOccasionGuide(occasionGuide) ? occasionGuide.guidelines : {
          structure: 'natural and flowing',
          length: 'moderate',
          style: 'personal and meaningful',
          focus: 'relationship and occasion',
          avoid: 'clichés and generic phrases'
        }
      };

      const { data, error } = await supabase.functions.invoke('generate-message', {
        body: messageParams
      });
      
      if (error) throw error;
      setMessage(data.message);
    } catch (error) {
      logError('Error generating message:', error);
      setError('Failed to generate message. Please try again or write your own message.');
    } finally {
      setIsLoadingMessage(false);
    }
  }

  async function generateAllCardImages() {
    if (isLoadingImages) return;
    
    info('Starting card image generation');
    setIsLoadingImages(true);
    setCardImages([]);
    setLoadingProgress('Generating card designs...');
    
    try {
      // Validate required parameters before sending
      if (!occasion) {
        throw new Error('Missing occasion - please specify what this card is for');
      }
      if (!contact.relationship) {
        throw new Error('Missing relationship - please specify your relationship with the recipient');
      }
      if (!contact.card_preference) {
        throw new Error('Missing card preference - please specify the style of card you prefer');
      }
      
      // Determine gender from relationship if not provided
      let gender = contact.gender;
      
      if (!gender) {
        const relationship = contact.relationship.toLowerCase();
        // Common masculine relationships
        const masculineRelationships = ['father', 'dad', 'grandfather', 'grandpa', 'brother', 'son', 'uncle', 'nephew', 'husband', 'boyfriend'];
        // Common feminine relationships
        const feminineRelationships = ['mother', 'mom', 'grandmother', 'grandma', 'sister', 'daughter', 'aunt', 'niece', 'wife', 'girlfriend'];
        
        // Try to determine the gender from the relationship
        for (const masculine of masculineRelationships) {
          if (relationship.includes(masculine)) {
            gender = 'male';
            info(`Automatically determined gender as male based on relationship "${contact.relationship}"`);
            break;
          }
        }
        
        if (!gender) {
          for (const feminine of feminineRelationships) {
            if (relationship.includes(feminine)) {
              gender = 'female';
              info(`Automatically determined gender as female based on relationship "${contact.relationship}"`);
              break;
            }
          }
        }
      }
      
      // Create parameters object for API call
      const params = {
        occasion,
        relationship: contact.relationship,
        gender,
        cardPreference: contact.card_preference,
        num_outputs: 3
      };
      
      // Log the parameters being sent to help with debugging
      info('Sending card generation parameters:', params);
      
      const { data, error } = await supabase.functions.invoke('generate-card-image', {
        body: params
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Error from edge function');
      }
      
      if (Array.isArray(data?.imageUrls)) {
        setCardImages(data.imageUrls);
      } else {
        throw new Error(data?.error || 'Invalid response format from image generation');
      }
    } catch (error) {
      logError('Error generating card images:', error);
      // Display the specific error message to help users fix the issue
      setError(error instanceof Error ? error.message : 'Failed to generate card designs. Please try again.');
    } finally {
      setIsLoadingImages(false);
      setLoadingProgress('');
    }
  }

  async function fetchUserProfile() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      setUserId(session.user.id);

      const { data: profile, error } = await supabase
        .from('users')
        .select('signature_url')
        .eq('user_id', session.user.id)
        .single();

      if (error) throw error;
      setSignatureUrl(profile?.signature_url || null);
    } catch (error) {
      logError('Error fetching user profile:', error);
    }
  }

  if (showEditor && selectedCard) {
    return (
      <CardEditor
        cardImage={cardImages[parseInt(selectedCard.split('-')[1])]}
        message={message}
        recipientName={contact.name}
        occasion={occasion}
        signatureUrl={signatureUrl}
        recipientAddress={contact}
        onApprove={(data) => {
          if (onApproveCard) {
            onApproveCard(data);
          } else {
            onSelect(data.front, message);
          }
          onClose();
        }}
        onRequestChanges={() => setShowEditor(false)}
        onClose={() => onClose()}
        userId={userId}
        subscriptionStatus={subscriptionStatus}
        userCredits={userCredits}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl flex flex-col h-[90vh]">
        {/* Fixed Header */}
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold font-display text-brand-secondary">
            Select a Design for {contact.name}'s {occasion} Card
          </h2>
          <button 
            onClick={onClose}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message - if present */}
        {error && (
          <div className="flex-shrink-0 p-4 bg-red-50 border-b border-red-200 flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoadingImages || isLoadingMessage ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-12 h-12 text-brand-primary animate-spin mb-4" />
              <p className="text-brand-secondary text-lg mb-2">
                {loadingProgress || 'Preparing to generate card designs...'}
              </p>
              <div className="mt-4 max-w-md text-center text-sm text-brand-secondary opacity-75">
                We'll create some beautiful {contact.card_preference.toLowerCase()} designs 
                for your {contact.relationship.toLowerCase()}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 max-w-[600px] mx-auto">
              {cardImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                    selectedCard === `card-${index}` 
                      ? 'border-brand-primary shadow-lg scale-[1.02]' 
                      : 'border-transparent hover:border-brand-accent hover:scale-[1.01]'
                  }`}
                  onClick={() => setSelectedCard(`card-${index}`)}
                >
                  <img
                    src={imageUrl}
                    alt={`Card design ${index + 1}`}
                    className="w-full aspect-[4/3] object-cover"
                    loading="lazy"
                  />
                  {selectedCard === `card-${index}` && (
                    <div className="absolute inset-0 bg-brand-primary bg-opacity-10 flex items-center justify-center">
                      <div className="bg-brand-primary text-white px-3 py-1.5 rounded-full text-sm">
                        Selected
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fixed Footer with Navigation */}
        <div className="flex-shrink-0 border-t bg-white p-4">
          <div className="flex justify-between items-center max-w-4xl mx-auto w-full">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={() => selectedCard && setShowEditor(true)}
              disabled={!selectedCard || isLoadingMessage}
              className={`px-6 py-2 rounded-lg transition-all ${
                selectedCard && !isLoadingMessage
                  ? 'bg-brand-primary text-white hover:bg-brand-secondary'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoadingMessage ? 'Preparing Message...' : 'Customize Card'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}